<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-[9999] overflow-y-auto">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="closeModal"></div>

      <!-- Modal with Responsive Dimensions -->
      <div class="flex min-h-full items-center justify-center p-2 sm:p-4">
        <div
          class="relative w-full max-w-[900px] h-[95vh] sm:h-[700px] bg-white rounded-lg shadow-xl flex flex-col mx-2 sm:mx-0"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200"
          >
            <div>
              <h2 class="text-base sm:text-xl font-bold text-gray-900">
                AI-Generated Study Material
              </h2>
            </div>
            <div class="flex items-center space-x-3">
              <button @click="closeModal" class="text-gray-400 hover:text-gray-600 cursor-pointer">
                <XMarkIcon class="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="flex-1 flex items-center justify-center">
            <div class="text-center">
              <div
                class="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center"
              >
                <svg class="w-8 h-8 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Loading Details...</h3>
              <p class="text-gray-500">Please wait while we fetch the complete information.</p>
            </div>
          </div>

          <!-- Content -->
          <div v-else class="flex-1 overflow-hidden flex flex-col">
            <!-- Metadata -->
            <div class="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
                <h3 class="text-lg sm:text-xl font-semibold text-gray-800">
                  {{ displayMaterial?.title }}
                </h3>
                <span
                  :class="[
                    'px-3 py-1 text-xs sm:text-sm font-medium rounded-full w-fit',
                    getTypeClasses(displayMaterial?.type),
                  ]"
                >
                  {{ displayMaterial?.type }}
                </span>
              </div>

              <div
                class="flex flex-col sm:flex-row sm:items-center sm:space-x-6 text-xs sm:text-sm text-gray-600 gap-2 sm:gap-0"
              >
                <div class="flex items-center">
                  <BookOpenIcon class="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  {{ displayMaterial?.subject }}
                </div>
                <div class="flex items-center">
                  <CalendarIcon class="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  {{ formattedDate }}
                </div>
                <div class="flex items-center">
                  <DocumentTextIcon class="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  {{ displayMaterial?.source }}
                </div>
              </div>
            </div>

            <!-- Tab Navigation -->
            <div class="border-b border-gray-200 bg-white px-3 sm:px-6">
              <div class="flex">
                <button
                  @click="activeTab = 'content'"
                  :class="[
                    'px-4 sm:px-6 py-3 font-medium text-xs sm:text-sm border-b-2 transition-colors cursor-pointer',
                    activeTab === 'content'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300',
                  ]"
                >
                  AI-Generated Content
                </button>
                <button
                  @click="activeTab = 'notes'"
                  :class="[
                    'px-4 sm:px-6 py-3 font-medium text-xs sm:text-sm border-b-2 transition-colors cursor-pointer',
                    activeTab === 'notes'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300',
                  ]"
                >
                  Original Student Notes
                </button>
              </div>
            </div>

            <!-- Scrollable Content -->
            <div class="flex-1 overflow-y-auto px-3 sm:px-6 md:px-12 py-4 sm:py-8">
              <!-- AI-Generated Content Tab -->
              <div v-if="activeTab === 'content'" class="space-y-6">
                <!-- Summary Type Content -->
                <template v-if="displayMaterial?.type === 'Summary'">
                  <!-- Summary Section -->
                  <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-blue-900 mb-3">Summary</h4>
                    <div
                      class="text-blue-800 text-sm leading-relaxed whitespace-pre-wrap katex-content"
                      v-html="renderedDescription"
                    ></div>
                  </div>

                  <!-- Key Points Section -->
                  <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 class="text-lg font-semibold text-green-900 mb-3">Key Points</h4>
                    <ul class="text-green-800 text-sm space-y-2">
                      <li
                        v-for="(point, index) in renderedKeyPoints"
                        :key="index"
                        class="flex items-start"
                      >
                        <span class="text-green-600 mr-2 mt-1">•</span>
                        <span class="katex-content" v-html="point"></span>
                      </li>
                    </ul>
                  </div>

                  <!-- Sources Section for Summaries -->
                  <div
                    v-if="displaySources.length > 0"
                    class="bg-orange-50 border border-orange-200 rounded-lg p-4"
                  >
                    <h4 class="text-lg font-semibold text-orange-900 mb-3">
                      Additional Sources & References
                    </h4>
                    <div
                      class="text-sm text-orange-900 mb-3 p-3 bg-orange-200/50 rounded border-l-4 border-orange-600"
                    >
                      <strong>AI Content Notice:</strong> The content provided was generated by AI
                      and cross-referenced among the additional sources below. Always verify
                      information independently for academic use.
                    </div>
                    <ul class="text-gray-700 text-sm space-y-3 ml-7">
                      <li
                        v-for="(source, index) in displaySources"
                        :key="index"
                        class="flex items-start"
                      >
                        <span class="text-orange-600 mr-2 mt-1 flex-shrink-0"
                          >{{ index + 1 }}.</span
                        >
                        <span
                          class="break-words break-all overflow-wrap-anywhere"
                          v-html="formatSourceWithLinks(source)"
                        ></span>
                      </li>
                    </ul>
                  </div>
                </template>

                <!-- Flashcards Type Content -->
                <template v-else-if="displayMaterial?.type === 'Flashcards'">
                  <!-- Interactive Flashcard Viewer -->
                  <div
                    class="mb-6 sm:mb-8 border-2 border-green-800 rounded-md p-3 sm:p-6 bg-green-100/25"
                  >
                    <div class="flex items-center justify-between mb-4 sm:mb-6">
                      <div class="flex items-center">
                        <svg
                          class="w-4 h-4 sm:w-5 sm:h-5 text-green-800 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                        <h3 class="text-base sm:text-lg font-semibold text-green-800">
                          Interactive Flashcards
                        </h3>
                      </div>

                      <!-- Card Counter -->
                      <div class="text-sm sm:text-base text-green-800 font-medium">
                        {{ currentCardIndex + 1 }} / {{ displayMaterial?.flashcards?.length || 0 }}
                      </div>
                    </div>

                    <!-- Flashcard Display Area -->
                    <div
                      v-if="displayMaterial?.flashcards && displayMaterial.flashcards.length > 0"
                      class="mb-4 sm:mb-6"
                    >
                      <!-- Single Flashcard Container -->
                      <div class="flex justify-center mb-4 sm:mb-6">
                        <div
                          class="flashcard-container relative w-full max-w-2xl h-48 sm:h-64 cursor-pointer"
                          @click="flipCard"
                          style="perspective: 1000px"
                        >
                          <div
                            class="flashcard w-full h-full relative transition-transform duration-700 preserve-3d"
                            :class="{ 'is-flipped': isFlipped }"
                            style="transform-style: preserve-3d"
                          >
                            <!-- Front of Card -->
                            <div
                              class="flashcard-face flashcard-front absolute inset-0 w-full h-full bg-white border-2 sm:border-3 border-black rounded-lg sm:rounded-xl p-4 sm:p-6 flex items-center justify-center shadow-[4px_4px_0_0_rgba(0,0,0,1)] sm:shadow-[6px_6px_0_0_rgba(0,0,0,1)]"
                              style="backface-visibility: hidden"
                            >
                              <div class="text-center max-w-full overflow-y-auto">
                                <div
                                  class="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3 font-medium"
                                >
                                  FRONT
                                </div>
                                <div
                                  class="text-base sm:text-lg font-bold text-gray-900 leading-relaxed katex-content"
                                  v-html="renderedCurrentCard?.front || 'No question available'"
                                ></div>
                                <div class="text-xs text-gray-400 mt-3 sm:mt-4">
                                  Click to reveal answer
                                </div>
                              </div>
                            </div>

                            <!-- Back of Card -->
                            <div
                              class="flashcard-face flashcard-back absolute inset-0 w-full h-full bg-gradient-to-br from-green-50 to-green-100 border-2 sm:border-3 border-green-600 rounded-lg sm:rounded-xl p-4 sm:p-6 flex items-center justify-center shadow-[4px_4px_0_0_rgba(34,197,94,1)] sm:shadow-[6px_6px_0_0_rgba(34,197,94,1)]"
                              style="backface-visibility: hidden; transform: rotateY(180deg)"
                            >
                              <div class="text-center max-w-full overflow-y-auto">
                                <div
                                  class="text-xs sm:text-sm text-green-600 mb-2 sm:mb-3 font-medium"
                                >
                                  BACK
                                </div>
                                <div
                                  class="text-sm sm:text-base font-semibold text-gray-900 leading-relaxed katex-content"
                                  v-html="renderedCurrentCard?.back || 'No answer available'"
                                ></div>
                                <div class="text-xs text-green-600 mt-3 sm:mt-4">
                                  Click to show question
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Navigation Controls -->
                      <div
                        class="flex justify-center items-center space-x-2 sm:space-x-4 flex-wrap gap-2"
                      >
                        <!-- Previous Button -->
                        <button
                          @click.stop="previousCard"
                          :disabled="currentCardIndex === 0"
                          :class="[
                            'flex items-center px-3 sm:px-4 py-2 rounded-lg font-bold text-white shadow-[3px_3px_0_0_rgba(0,0,0,1)] sm:shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black transition-all duration-200 text-xs sm:text-sm',
                            currentCardIndex === 0
                              ? 'bg-gray-400 cursor-not-allowed opacity-50'
                              : 'bg-blue-600 hover:bg-blue-700 hover:transform hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[4px_4px_0_0_rgba(0,0,0,1)] sm:hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] cursor-pointer',
                          ]"
                        >
                          <svg
                            class="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                              clip-rule="evenodd"
                            />
                          </svg>
                          <span class="hidden sm:inline">Previous</span>
                          <span class="sm:hidden">Prev</span>
                        </button>

                        <!-- Card Flip Button -->
                        <button
                          @click.stop="flipCard"
                          class="flex items-center px-3 sm:px-4 py-2 rounded-lg font-bold text-white bg-green-600 hover:bg-green-700 shadow-[3px_3px_0_0_rgba(0,0,0,1)] sm:shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black transition-all duration-200 hover:transform hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[4px_4px_0_0_rgba(0,0,0,1)] sm:hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] cursor-pointer text-xs sm:text-sm"
                        >
                          <svg
                            class="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                              clip-rule="evenodd"
                            />
                          </svg>
                          <span class="hidden sm:inline">{{
                            isFlipped ? "Show Question" : "Show Answer"
                          }}</span>
                          <span class="sm:hidden">Flip</span>
                        </button>

                        <!-- Next Button -->
                        <button
                          @click.stop="nextCard"
                          :disabled="
                            currentCardIndex === (displayMaterial?.flashcards?.length || 1) - 1
                          "
                          :class="[
                            'flex items-center px-3 sm:px-4 py-2 rounded-lg font-bold text-white shadow-[3px_3px_0_0_rgba(0,0,0,1)] sm:shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black transition-all duration-200 text-xs sm:text-sm',
                            currentCardIndex === (displayMaterial?.flashcards?.length || 1) - 1
                              ? 'bg-gray-400 cursor-not-allowed opacity-50'
                              : 'bg-blue-600 hover:bg-blue-700 hover:transform hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[4px_4px_0_0_rgba(0,0,0,1)] sm:hover:shadow-[6px_6px_0_0_rgba(0,0,0,1)] cursor-pointer',
                          ]"
                        >
                          <span class="hidden sm:inline">Next</span>
                          <span class="sm:hidden">Next</span>
                          <svg
                            class="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                              clip-rule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>

                      <!-- Progress Bar -->
                      <div class="mt-4 sm:mt-6">
                        <div class="w-full bg-gray-200 rounded-full h-2">
                          <div
                            class="bg-green-600 h-2 rounded-full transition-all duration-300"
                            :style="{ width: progressPercentage + '%' }"
                          ></div>
                        </div>
                        <div class="text-center text-xs sm:text-sm text-gray-600 mt-2">
                          Progress: {{ progressPercentage.toFixed(0) }}%
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Flashcards List -->
                  <div class="space-y-3 sm:space-y-4">
                    <div
                      v-for="(flashcard, index) in displayMaterial?.flashcards"
                      :key="index"
                      class="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-5 hover:shadow-md transition-shadow"
                    >
                      <div class="mb-2 sm:mb-3">
                        <span
                          class="inline-block px-2 sm:px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full mb-2"
                        >
                          Card {{ index + 1 }}
                        </span>
                        <h5
                          class="text-base sm:text-lg font-bold text-green-900 mb-2 katex-content"
                          v-html="renderedFlashcards[index]?.front"
                        ></h5>
                      </div>
                      <div class="bg-white/70 rounded-lg p-3 sm:p-4 border border-green-100">
                        <p
                          class="text-gray-800 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap katex-content"
                          v-html="renderedFlashcards[index]?.back"
                        ></p>
                      </div>
                    </div>
                  </div>

                  <!-- Sources Section for Flashcards -->
                  <div
                    v-if="displaySources.length > 0"
                    class="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4"
                  >
                    <h4 class="text-base sm:text-lg font-semibold text-orange-900 mb-3">
                      Additional Sources & References
                    </h4>
                    <div
                      class="text-xs sm:text-sm text-orange-900 mb-3 p-2 sm:p-3 bg-orange-200/50 rounded border-l-4 border-orange-600"
                    >
                      <strong>AI Content Notice:</strong> The content provided was generated by AI
                      and cross-referenced among the additional sources below. Always verify
                      information independently for academic use.
                    </div>
                    <ul class="text-gray-700 text-xs sm:text-sm space-y-3 ml-7">
                      <li
                        v-for="(source, index) in displaySources"
                        :key="index"
                        class="flex items-start"
                      >
                        <span class="text-orange-600 mr-2 mt-1 flex-shrink-0"
                          >{{ index + 1 }}.</span
                        >
                        <span
                          class="break-words break-all overflow-wrap-anywhere"
                          v-html="formatSourceWithLinks(source)"
                        ></span>
                      </li>
                    </ul>
                  </div>
                </template>

                <!-- AI Disclaimer -->
                <div
                  class="mb-4 sm:mb-6 bg-amber-50 border-2 border-amber-200 rounded-lg p-3 sm:p-4"
                >
                  <div class="flex items-start space-x-2 sm:space-x-3">
                    <div class="flex-shrink-0">
                      <svg
                        class="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 mt-0.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                    <div class="text-xs sm:text-sm text-amber-800">
                      <strong class="font-semibold">AI Content Notice:</strong> The AI-generated
                      content in this app is for reference only and may contain errors. We encourage
                      you to double-check the information and consult with your teachers or trusted
                      sources. As of October 2025, this app uses the Google Gemini 2.5 Pro model to
                      assist in processing notes. If you find anything unusual, please report it
                      using the button or email us at
                      <a
                        href="mailto:elevated.tutorapp@gmail.com"
                        class="underline hover:text-amber-900 break-all"
                        >elevated.tutorapp@gmail.com</a
                      >.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Original Student Notes Tab -->
              <div v-else-if="activeTab === 'notes'" class="space-y-4">
                <!-- Notes Metadata -->
                <div class="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span class="text-gray-600">Source Type:</span>
                      <span class="ml-2 font-medium text-gray-900">{{
                        getSourceTypeLabel(displayMaterial?.source_type || "note")
                      }}</span>
                    </div>
                    <div v-if="displayMaterial?.filename">
                      <span class="text-gray-600">Filename:</span>
                      <span class="ml-2 font-medium text-gray-900">{{
                        displayMaterial.filename
                      }}</span>
                    </div>
                    <div>
                      <span class="text-gray-600">Content Length:</span>
                      <span class="ml-2 font-medium text-gray-900"
                        >{{ displayMaterial?.input_length?.toLocaleString() || 0 }} characters</span
                      >
                    </div>
                    <div v-if="displayMaterial?.student_notes_url">
                      <a
                        :href="displayMaterial.student_notes_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1 cursor-pointer"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path
                            d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"
                          />
                        </svg>
                        Download Original File
                      </a>
                    </div>
                  </div>
                </div>

                <!-- Notes Content -->
                <div class="bg-white border-2 border-gray-200 rounded-lg p-5">
                  <div class="flex items-center gap-2 mb-3">
                    <svg class="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                      />
                    </svg>
                    <h3 class="text-lg font-semibold text-gray-900">Student Notes</h3>
                  </div>

                  <div v-if="displayMaterial?.student_notes" class="prose max-w-none">
                    <pre
                      class="whitespace-pre-wrap text-sm text-gray-700 font-sans bg-gray-50 p-4 rounded border border-gray-200 max-h-96 overflow-y-auto"
                      >{{ displayMaterial.student_notes }}</pre
                    >
                  </div>
                  <div v-else class="text-center py-8 text-gray-500">
                    <svg
                      class="w-12 h-12 mx-auto mb-3 opacity-50"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10,19L12,15H9V10H13V12L11,16H14V19H10Z"
                      />
                    </svg>
                    <p>No text notes available for this material.</p>
                    <p class="text-sm mt-1">The student may have uploaded a file instead.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { XMarkIcon, BookOpenIcon, CalendarIcon, DocumentTextIcon } from "@heroicons/vue/24/outline";
import { computed, ref, watch } from "vue";
import { db, doc, getDoc } from "@/firebase";
import type { SummaryRecord } from "@/services/summaryService";
import type { FlashcardRecord } from "@/services/flashcardService";
import type { Flashcard } from "@/services/geminiService";
import { logDebug, logInfo, logError, logSuccess } from "@/utils/secureLogger";
import { renderLatex } from "@/utils/katexRenderer";

interface AIStudyMaterial {
  id: string;
  title: string;
  type: "Summary" | "Flashcards" | "Quiz";
  subject: string;
  source: string;
  date: Date | string; // Allow both Date object and string
  description: string;
  keyPoints: string;
  keyPointsList?: string[];
  flashcards?: Flashcard[]; // For flashcard data
  student_notes?: string; // ✅ Original pasted/text notes
  student_notes_url?: string; // ✅ Firebase Storage URL for uploaded files
  input_length?: number; // ✅ Content length in characters
  filename?: string; // ✅ Original filename (if uploaded)
  source_type?: "pdf" | "txt" | "docx" | "note"; // ✅ Source type
}

interface Props {
  material: AIStudyMaterial | null;
  isOpen: boolean;
}

const props = defineProps<Props>();

// Reactive data for Firebase integration
const isLoading = ref(false);
const fullSummaryData = ref<SummaryRecord | null>(null);
const fullFlashcardData = ref<FlashcardRecord | null>(null);

// Interactive flashcard state
const currentCardIndex = ref(0);
const isFlipped = ref(false);

// Tab navigation state
const activeTab = ref<"content" | "notes">("content");

// Computed property for the material to display (either full Firebase data or original material)
const displayMaterial = computed(() => {
  if (props.material?.type === "Summary" && fullSummaryData.value) {
    return {
      ...props.material,
      description: fullSummaryData.value.summary_text, // Show full summary
      keyPointsList: fullSummaryData.value.key_points, // Show all key points
      student_notes: fullSummaryData.value.student_notes, // ✅ Add original text notes
      student_notes_url: fullSummaryData.value.student_notes_url, // ✅ Add file URL
      input_length: fullSummaryData.value.input_length, // ✅ Add content length
      filename: fullSummaryData.value.filename, // ✅ Add filename
      source_type: fullSummaryData.value.source_type, // ✅ Add source type
    };
  } else if (props.material?.type === "Flashcards" && fullFlashcardData.value) {
    return {
      ...props.material,
      description: `${fullFlashcardData.value.flashcard_data.length} interactive flashcards for ${fullFlashcardData.value.topic}`,
      flashcards: fullFlashcardData.value.flashcard_data,
      student_notes: fullFlashcardData.value.student_notes, // ✅ Add original text notes
      student_notes_url: fullFlashcardData.value.student_notes_url, // ✅ Add file URL
      input_length: fullFlashcardData.value.input_length, // ✅ Add content length
      filename: fullFlashcardData.value.filename, // ✅ Add filename
      source_type: fullFlashcardData.value.source_type, // ✅ Add source type
    };
  }
  return props.material;
});

// Computed property for rendered LaTeX content
const renderedDescription = computed(() => {
  if (!displayMaterial.value?.description) return "";
  return renderLatex(displayMaterial.value.description);
});

const renderedKeyPoints = computed(() => {
  if (!displayMaterial.value?.keyPointsList) return [];
  return displayMaterial.value.keyPointsList.map((point) => renderLatex(point));
});

const renderedCurrentCard = computed(() => {
  if (!currentCard.value) return null;
  return {
    front: renderLatex(currentCard.value.front),
    back: renderLatex(currentCard.value.back),
  };
});

const renderedFlashcards = computed(() => {
  if (!displayMaterial.value?.flashcards) return [];
  return displayMaterial.value.flashcards.map((card) => ({
    front: renderLatex(card.front),
    back: renderLatex(card.back),
  }));
});

// Computed property for sources
const displaySources = computed(() => {
  if (props.material?.type === "Summary" && fullSummaryData.value) {
    return (
      fullSummaryData.value.sources || ["AI-generated content - verify with authoritative sources"]
    );
  } else if (props.material?.type === "Flashcards" && fullFlashcardData.value) {
    return (
      fullFlashcardData.value.sources || [
        "AI-generated flashcards - verify with authoritative sources",
      ]
    );
  }
  return [];
});

// Computed properties for interactive flashcards
const currentCard = computed(() => {
  if (!displayMaterial.value?.flashcards) return null;
  return displayMaterial.value.flashcards[currentCardIndex.value];
});

const progressPercentage = computed(() => {
  if (!displayMaterial.value?.flashcards || displayMaterial.value.flashcards.length === 0) {
    return 0;
  }
  return ((currentCardIndex.value + 1) / displayMaterial.value.flashcards.length) * 100;
});

// Methods for flashcard interaction
const flipCard = () => {
  isFlipped.value = !isFlipped.value;
};

const nextCard = () => {
  if (
    displayMaterial.value?.flashcards &&
    currentCardIndex.value < displayMaterial.value.flashcards.length - 1
  ) {
    currentCardIndex.value++;
    isFlipped.value = false; // Reset flip state when changing cards
  }
};

const previousCard = () => {
  if (currentCardIndex.value > 0) {
    currentCardIndex.value--;
    isFlipped.value = false; // Reset flip state when changing cards
  }
};

// Format date to the required format: Sept. 2, 2025 - 2:10PM
const formattedDate = computed(() => {
  if (!displayMaterial.value?.date) return "";

  const date =
    displayMaterial.value.date instanceof Date
      ? displayMaterial.value.date
      : new Date(displayMaterial.value.date);

  const monthNames = [
    "Jan.",
    "Feb.",
    "Mar.",
    "Apr.",
    "May",
    "Jun.",
    "Jul.",
    "Aug.",
    "Sept.",
    "Oct.",
    "Nov.",
    "Dec.",
  ];

  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  return `${month} ${day}, ${year} - ${hours}:${minutes}${ampm}`;
});

// Fetch full summary data from Firebase
const fetchFullSummaryData = async (summaryId: string) => {
  try {
    isLoading.value = true;
    logInfo("Fetching full summary data from Firebase", {
      summaryId: summaryId.substring(0, 8) + "***",
    });

    const summaryDoc = await getDoc(doc(db, "summaries", summaryId));

    if (summaryDoc.exists()) {
      const data = summaryDoc.data() as Omit<SummaryRecord, "summary_id">;
      fullSummaryData.value = {
        summary_id: summaryDoc.id,
        ...data,
      };
      logSuccess("Full summary data fetched successfully");
    } else {
      logError("Summary document not found in Firebase", {
        summaryId: summaryId.substring(0, 8) + "***",
      });
    }
  } catch (error) {
    logError("Error fetching full summary data", error);
  } finally {
    isLoading.value = false;
  }
};

// Fetch full flashcard data from Firebase
const fetchFullFlashcardData = async (flashcardId: string) => {
  try {
    isLoading.value = true;
    logInfo("Fetching full flashcard data from Firebase", {
      flashcardId: flashcardId.substring(0, 8) + "***",
    });

    const flashcardDoc = await getDoc(doc(db, "flashcards", flashcardId));

    if (flashcardDoc.exists()) {
      const data = flashcardDoc.data() as Omit<FlashcardRecord, "flashcards_id">;
      fullFlashcardData.value = {
        flashcards_id: flashcardDoc.id,
        ...data,
      };
      logSuccess("Full flashcard data fetched successfully", {
        flashcardCount: fullFlashcardData.value.flashcard_data?.length || 0,
        topic: fullFlashcardData.value.topic,
      });
    } else {
      logError("Flashcard document not found in Firebase", {
        flashcardId: flashcardId.substring(0, 8) + "***",
      });
    }
  } catch (error) {
    logError("Error fetching full flashcard data", error);
  } finally {
    isLoading.value = false;
  }
};

// Watch for modal opening and material changes to fetch full data
watch(
  [() => props.isOpen, () => props.material],
  async ([isOpen, material]) => {
    if (isOpen && material) {
      if (material.type === "Summary") {
        logDebug("Modal opened for Summary type, fetching full data from Firebase");
        fullFlashcardData.value = null; // Clear flashcard data
        await fetchFullSummaryData(material.id);
      } else if (material.type === "Flashcards") {
        logDebug("Modal opened for Flashcards type, fetching full data from Firebase");
        fullSummaryData.value = null; // Clear summary data
        await fetchFullFlashcardData(material.id);
        // Reset flashcard viewer state
        currentCardIndex.value = 0;
        isFlipped.value = false;
      }
    } else if (!isOpen) {
      // Clear full data when modal closes
      fullSummaryData.value = null;
      fullFlashcardData.value = null;
      // Reset flashcard viewer state
      currentCardIndex.value = 0;
      isFlipped.value = false;
    }
  },
  { immediate: true }
);

const emit = defineEmits<{
  close: [];
}>();

const closeModal = () => {
  emit("close");
};

const getTypeClasses = (type: string | undefined) => {
  switch (type) {
    case "Summary":
      return "border border-purple-800 bg-purple-100 text-purple-800";
    case "Flashcards":
      return "border border-green-800 bg-green-100 text-green-800";
    case "Quiz":
      return "border border-orange-800 bg-orange-100 text-orange-800";
    default:
      return "border border-gray-800 bg-gray-100 text-gray-800";
  }
};

const getSourceTypeLabel = (sourceType: string): string => {
  const labels: Record<string, string> = {
    pdf: "PDF Document",
    txt: "Text File",
    docx: "Word Document",
    note: "Pasted Text",
  };
  return labels[sourceType] || sourceType;
};

// Sanitize HTML to prevent XSS attacks
const sanitizeHtml = (html: string): string => {
  const div = document.createElement("div");
  div.textContent = html;
  return div.innerHTML;
};

// Format source text in full APA 7th Edition format
const formatSourceWithLinks = (source: string): string => {
  if (!source) return "";

  // Extract URL from source string
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  const urls = source.match(urlRegex);

  if (!urls || urls.length === 0) {
    return sanitizeHtml(source);
  }

  const url = urls[0];

  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.replace("www.", "");

    // Get current date for retrieval date
    const now = new Date();
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const retrievalDate = `${months[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;

    // Extract organization/author from domain
    let author = "";
    if (domain.includes(".edu")) {
      // Educational institutions
      const parts = domain.split(".");
      author = parts[0]
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    } else if (domain.includes(".gov")) {
      // Government sites
      const parts = domain.split(".");
      author = parts[0]
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    } else {
      // Default: use domain as author
      author = domain.split(".")[0].charAt(0).toUpperCase() + domain.split(".")[0].slice(1);
    }

    // Extract title from source (text before URL) or use domain-based title
    let title = source.split(url)[0].trim();
    if (!title || title.length < 5) {
      title = "Educational resource";
    }

    // Build APA 7th Edition citation
    // Format: Author. (Year). Title. Retrieved Month Day, Year, from URL
    const apaFormatted = `
      <span class="font-semibold">${sanitizeHtml(author)}.</span>
      <span class="italic">${sanitizeHtml(title)}</span>.
      Retrieved ${retrievalDate}, from
      <a href="${url}" target="_blank" rel="noopener noreferrer" 
         class="text-blue-600 hover:text-blue-800 underline inline-block ml-1" 
         style="word-break: break-all; overflow-wrap: anywhere;">${url}</a>
    `;

    return apaFormatted;
  } catch (error) {
    // Fallback: Just make the URL clickable
    const fallback = source.replace(
      urlRegex,
      (matchedUrl) =>
        `<a href="${matchedUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline inline-block" style="word-break: break-all; overflow-wrap: anywhere;">${matchedUrl}</a>`
    );
    return fallback;
  }
};
</script>

<style scoped>
/* 3D Flashcard Flip Animation */
.flashcard-container {
  perspective: 1000px;
}

.flashcard {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.7s ease-in-out;
  transform-style: preserve-3d;
}

.flashcard.is-flipped {
  transform: rotateY(180deg);
}

.flashcard-face {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.flashcard-front {
  transform: rotateY(0deg);
}

.flashcard-back {
  transform: rotateY(180deg);
}

.preserve-3d {
  transform-style: preserve-3d;
}

/* KaTeX Formula Styling */
.katex-content {
  line-height: 1.8;
}

.katex-content .katex {
  font-size: 1.1em;
}

.katex-content .katex-display {
  margin: 1em 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.katex-content .katex-display > .katex {
  text-align: center;
}
</style>
